import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { prepareAvatarUpload } from '../utils/avatarUpload';
import { Avatar } from './Avatar';

export type PhotoChange =
  | { kind: 'unchanged' }
  | { kind: 'replace'; blob: Blob; previewUrl: string }
  | { kind: 'remove' };

interface PhotoPickerProps {
  name: string;
  currentSrc: string | null;
  value: PhotoChange;
  onChange: (next: PhotoChange) => void;
  disabled?: boolean;
  variant?: 'form' | 'header';
}

export function PhotoPicker({
  name,
  currentSrc,
  value,
  onChange,
  disabled = false,
  variant = 'form',
}: PhotoPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current);
      }
    },
    [],
  );

  const previewSrc =
    value.kind === 'replace' ? value.previewUrl : value.kind === 'remove' ? null : currentSrc;
  const header = variant === 'header';
  const buttonClass = header ? 'link-button' : 'btn-secondary';
  const busy = disabled || processing;

  function releasePreview() {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
  }

  async function pick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setProcessing(true);
    setProblem(null);
    try {
      const blob = await prepareAvatarUpload(file);
      releasePreview();
      const previewUrl = URL.createObjectURL(blob);
      previewRef.current = previewUrl;
      onChange({ kind: 'replace', blob, previewUrl });
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setProcessing(false);
    }
  }

  function remove() {
    setProblem(null);
    if (value.kind === 'replace') {
      releasePreview();
      onChange({ kind: 'unchanged' });
      return;
    }
    onChange({ kind: 'remove' });
  }

  return (
    <div className={`photo-picker${header ? ' photo-picker-header' : ''}`}>
      <Avatar name={name} photoSrc={previewSrc} size={header ? 'lg' : 'xl'} />
      <div className="photo-picker-body">
        <div className="photo-picker-actions">
          <button
            type="button"
            className={buttonClass}
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {processing ? 'Processing…' : previewSrc ? 'Change photo' : 'Upload photo'}
          </button>
          {previewSrc && (
            <button type="button" className={buttonClass} disabled={busy} onClick={remove}>
              {value.kind === 'replace' ? 'Discard' : 'Remove'}
            </button>
          )}
        </div>
        {!header && !problem && (
          <div className="photo-picker-hint">JPEG, PNG or WebP up to 10 MB — cropped to a square.</div>
        )}
        {problem && <div className="photo-picker-error">{problem}</div>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(event) => void pick(event)}
        hidden
      />
    </div>
  );
}
